// 20260506 -- Copilot (Claude Sonnet 4.6) -- Frontend service for task sharing API calls

/**
 * ShareService - Axios wrappers for the task sharing endpoints in /api/v3/share.
 *
 * CREATED: 20260506
 * WHO: Copilot (Claude Sonnet 4.6)
 * WHY: Centralizes share API calls, following the TaskService pattern used across the app.
 *
 * USAGE:
 *   import ShareService from '@/services/ShareService'
 *   const { data } = await ShareService.createShare({ taskId, expirationDays, includeSubtasks }, token)
 *   const { data } = await ShareService.getShares(token)
 *   const { data } = await ShareService.revokeShare(shareToken, token)
 */

import axios from 'axios';

const BASE_URL = '/api/v3/share';

class ShareService {

  /**
   * createShare - Generates a new share link for a task.
   *
   * @param {Object} options
   * @param {string}  options.taskId          - Task _id to share
   * @param {number}  options.expirationDays  - 1–90 days (default: 5)
   * @param {boolean} options.includeSubtasks - Whether to snapshot subtasks (default: true)
   * @param {string} token - Bearer token
   * @returns {Promise} Axios response with { successful, data: { token, expiresAt, createdAt } }
   */
  static createShare({ taskId, expirationDays = 5, includeSubtasks = true }, token) {
    return axios.post(BASE_URL, { taskId, expirationDays, includeSubtasks }, {
      headers: { Authorization: `Bearer ${token}` },
    });
  }

  /**
   * getShares - Returns all non-revoked share links for the authenticated user.
   *
   * @param {string} token - Bearer token
   * @returns {Promise} Axios response with { successful, data: [{ token, taskId, taskTitle, status, expiresAt, createdAt }] }
   */
  static getShares(token) {
    return axios.get(BASE_URL, {
      headers: { Authorization: `Bearer ${token}` },
    });
  }

  /**
   * revokeShare - Revokes a share link by token.
   *
   * @param {string} shareToken - The 4-word token to revoke
   * @param {string} token      - Bearer token
   * @returns {Promise} Axios response with { successful, message }
   */
  static revokeShare(shareToken, token) {
    return axios.delete(`${BASE_URL}/${shareToken}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
  }
}

export default ShareService;
